import { cva, cx, VariantProps } from 'class-variance-authority';
import { DetailedHTMLProps, forwardRef, InputHTMLAttributes, ReactNode } from 'react';

const checkboxClassNames = cva(
    'tw-w-4 tw-h-4 tw-text-success tw-bg-accents-1 tw-border-accents-2 tw-rounded-sm focus:tw-ring-success focus:tw-ring-2'
);

export interface StyledCheckboxProps
    extends
        Omit<
            DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
            'onChange' | keyof VariantProps<typeof checkboxClassNames>
        >,
        VariantProps<typeof checkboxClassNames> {
    isSelected?: boolean;
    onChange?: (checked: boolean) => void;
    label?: ReactNode;
    /** @deprecated Use `label` prop instead */
    disableAnimation?: boolean;
    /** @deprecated Use className prop instead */
    css?: any;
    isDisabled?: boolean;
    isInvalid?: boolean;
    children?: React.ReactNode;
}

const StyledCheckbox = forwardRef<HTMLInputElement, StyledCheckboxProps>(
    (
        {
            label,
            name,
            id,
            isSelected,
            onChange,
            isDisabled,
            isInvalid,
            disableAnimation,
            css,
            children,
            defaultChecked,
            ...props
        },
        ref
    ) => (
        <div className="tw-relative tw-flex tw-gap-2">
            <div className="tw-flex tw-shrink-0 tw-items-center">
                <div className="group tw-grid tw-size-5 tw-grid-cols-1">
                    <input
                        {...(typeof isSelected === 'boolean'
                            ? { checked: isSelected }
                            : defaultChecked !== undefined
                              ? { defaultChecked }
                              : {})}
                        id={id}
                        name={name}
                        onChange={(e) => onChange?.(e.target.checked)}
                        type="checkbox"
                        className={cx(
                            isInvalid ? 'tw-border-error tw-bg-errorLight' : 'tw-border-accents-2 tw-bg-white',
                            'tw-col-start-1 tw-row-start-1 tw-appearance-none tw-rounded-md tw-border tw-border-solid checked:tw-border-success checked:tw-bg-success indeterminate:tw-border-success indeterminate:tw-bg-success focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-success disabled:tw-border-accents-2 disabled:tw-bg-accents-2 disabled:checked:tw-bg-accents-5 forced-colors:tw-appearance-auto'
                        )}
                        aria-invalid={isInvalid || undefined}
                        ref={ref}
                        {...props}
                        disabled={isDisabled || props?.disabled}
                    />
                    <svg
                        fill="none"
                        viewBox="0 0 14 14"
                        className="tw-pointer-events-none tw-col-start-1 tw-row-start-1 tw-size-3.5 tw-self-center tw-justify-self-center tw-stroke-white group-has-[:disabled]:tw-stroke-white/25"
                    >
                        <path
                            d="M3 8L6 11L11 3.5"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={cx(
                                isDisabled || (props?.disabled && 'tw-opacity-0'),
                                'group-has-[:checked]:tw-opacity-100'
                            )} //
                        />
                        <path
                            d="M3 7H11"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="tw-opacity-0 group-has-[:indeterminate]:tw-opacity-100"
                        />
                    </svg>
                </div>
            </div>
            {(label || children) && (
                <div
                    className="tw-min-w-0 tw-flex-1 tw-text-sm/6"
                    onClick={() => (isDisabled || props?.disabled ? undefined : onChange?.(!isSelected))}
                >
                    <label
                        htmlFor={id}
                        className={cx(
                            'tw-mb-0 tw-select-none tw-font-medium',
                            isInvalid ? 'tw-text-error' : 'tw-text-accents-9'
                        )}
                    >
                        {label || children}
                    </label>
                </div>
            )}
        </div>
    )
);

StyledCheckbox.displayName = 'StyledCheckbox';

export default StyledCheckbox;
